import { useQueries } from '@tanstack/react-query';
import { createFileRoute, useBlocker, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/Button';
import { ErrorBanner } from '@/components/ErrorBanner';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { ShareWorkoutActions } from '@/components/community/ShareWorkoutActions';
import { DiscardSessionDialog } from '@/components/workout/DiscardSessionDialog';
import { ExerciseBlock } from '@/components/workout/ExerciseBlock';
import { ExerciseQuickSearch } from '@/components/workout/ExerciseQuickSearch';
import { RestTimerBar } from '@/components/workout/RestTimerBar';
import { SessionHeader } from '@/components/workout/SessionHeader';
import { SessionSidePanel } from '@/components/workout/SessionSidePanel';
import { SessionSummaryCard } from '@/components/workout/SessionSummaryCard';
import { SyncBadge } from '@/components/workout/SyncBadge';
import { defaultDraft, isCardioTarget, validDraft } from '@/components/workout/draft';
import { sessionTitle } from '@/components/workout/format';
import type { SetDraft } from '@/components/workout/SetRow';
import { DEFAULT_REST_SECONDS, useRestTimer } from '@/components/workout/useRestTimer';
import { describeError } from '@/lib/api';
import { safeVolume, summarizeSession, type SessionSummary } from '@/lib/offline/coalesce';
import type { LocalSessionTarget, LocalSet } from '@/lib/offline/db';
import { isEmptyOpenSession } from '@/lib/offline/discard';
import {
  addSessionTarget,
  discardSession,
  finishSession,
  logSet,
  removeSet,
} from '@/lib/offline/session';
import { exerciseBestOptions } from '@/lib/queries/progress';
import { emitTourSignal } from '@/lib/tour/signals';
import { useLocalSession, useLocalSets } from '@/lib/queries/workouts';

export const Route = createFileRoute('/_app/train/session/$clientId')({
  component: SessionPage,
});

function SessionPage() {
  const { clientId } = Route.useParams();
  const navigate = useNavigate();
  const session = useLocalSession(clientId);
  const sets = useLocalSets(clientId);
  const timer = useRestTimer();

  const [drafts, setDrafts] = useState<Record<string, SetDraft>>({});
  const [extras, setExtras] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [summary, setSummary] = useState<SessionSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [askDiscard, setAskDiscard] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [discardError, setDiscardError] = useState<string | null>(null);

  // Salir de una sesión sin ninguna serie pregunta si se descarta: si no, quien
  // sólo entraba a mirar dejaba sesiones vacías abiertas en el servidor. Va en
  // referencias: el bloqueo se consulta en el momento de navegar, y al
  // descartar navegamos antes de que React repinte.
  const empty = isEmptyOpenSession(session, sets?.length ?? 0);
  const emptyRef = useRef(empty);
  const leavingRef = useRef(false);
  useEffect(() => {
    emptyRef.current = empty;
  }, [empty]);
  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) =>
      emptyRef.current && !leavingRef.current && current.pathname !== next.pathname,
    // Sin el aviso genérico del navegador: recargar no es salir de la sesión, y
    // al cerrar la pestaña no se podría ofrecer «Descartar» de todos modos (el
    // historial la marca «Sin terminar» y se descarta desde allí).
    enableBeforeUnload: false,
    withResolver: true,
  });
  const blocked = blocker.status === 'blocked';
  const { reset: resetBlocker } = blocker;

  const keepSession = useCallback(() => {
    setDiscardError(null);
    setAskDiscard(false);
    resetBlocker?.();
  }, [resetBlocker]);

  async function handleDiscard(): Promise<void> {
    leavingRef.current = true;
    setDiscarding(true);
    setDiscardError(null);
    try {
      await discardSession(clientId);
      if (blocker.status === 'blocked') blocker.proceed();
      else await navigate({ to: '/train', replace: true });
    } catch (cause) {
      leavingRef.current = false;
      setDiscarding(false);
      setDiscardError(describeError(cause));
    }
  }

  /** Los objetivos del día, más cualquier ejercicio suelto que tenga series. */
  const targets = useMemo<LocalSessionTarget[]>(() => {
    if (session == null) return [];
    const list = [...session.targets].sort((a, b) => a.position - b.position);
    const known = new Set(list.map((target) => target.exerciseId));
    for (const set of sets ?? []) {
      if (known.has(set.exerciseId)) continue;
      known.add(set.exerciseId);
      list.push({
        exerciseId: set.exerciseId,
        exerciseName: set.exerciseName,
        exerciseMedia: set.exerciseMedia,
        position: list.length,
        targetSets: 1,
        targetRepsMin: null,
        targetRepsMax: null,
        targetRpe: null,
        restSeconds: null,
        notes: null,
        exerciseCategory: set.exerciseCategory ?? null,
        unilateral: set.unilateral ?? false,
      });
    }
    return list;
  }, [session, sets]);

  const byExercise = useMemo(() => {
    const map = new Map<string, LocalSet[]>();
    for (const set of sets ?? []) {
      const list = map.get(set.exerciseId);
      if (list) list.push(set);
      else map.set(set.exerciseId, [set]);
    }
    return map;
  }, [sets]);

  // Marca previa de cada ejercicio: se pide una vez y se congela durante la
  // sesión. Sin red falla en silencio y la pantalla sigue funcionando.
  const bestQueries = useQueries({
    queries: targets.map((target) => exerciseBestOptions(clientId, target.exerciseId)),
  });
  const bests = useMemo(() => {
    const map = new Map<string, (typeof bestQueries)[number]['data']>();
    targets.forEach((target, index) => map.set(target.exerciseId, bestQueries[index]?.data));
    return map;
  }, [targets, bestQueries]);

  const totalVolume = (sets ?? []).reduce((total, set) => total + safeVolume(set), 0);

  /** Series efectivas por ejercicio, para el índice del panel lateral. */
  const workingCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const [exerciseId, list] of byExercise) {
      map.set(exerciseId, list.filter((set) => set.setType !== 'warmup').length);
    }
    return map;
  }, [byExercise]);

  // Una sesión ya cerrada se lee en el historial, no aquí.
  useEffect(() => {
    if (session == null || session.finishedAt === null || summary !== null) return;
    void navigate({ to: '/train/history/$clientId', params: { clientId }, replace: true });
  }, [session, summary, navigate, clientId]);

  // Al descartar, la sesión desaparece de Dexie antes de salir: sin esto se
  // vería un instante el «no hemos encontrado este entrenamiento».
  if (discarding || session === undefined || sets === undefined) {
    return (
      <div className="flex justify-center py-10">
        <Spinner size="lg" />
      </div>
    );
  }

  if (session === null) {
    return (
      <>
        <PageHeader title="Entrenamiento" />
        <ErrorBanner
          title="No hemos encontrado este entrenamiento"
          message="Puede que se registrara en otro dispositivo. Búscalo en el historial."
        />
      </>
    );
  }

  if (summary !== null) {
    const started = new Date(session.startedAt).getTime();
    const ended = session.finishedAt === null ? Date.now() : new Date(session.finishedAt).getTime();
    return (
      <>
        <PageHeader title="Resumen" />
        <div className="flex flex-col gap-4" data-tour="session-summary">
          <SessionSummaryCard
            summary={summary}
            durationMs={Math.max(0, ended - started)}
            title={sessionTitle(session.routineName, session.dayName)}
          />
          <SyncBadge />
          <ShareWorkoutActions
            clientId={session.clientId}
            synced={session.syncedAt !== null}
            prs={summary.prs}
          />
          <Button size="lg" block onClick={() => void navigate({ to: '/train' })}>
            Volver a Entrenar
          </Button>
        </div>
      </>
    );
  }

  function draftFor(target: LocalSessionTarget): SetDraft | null {
    const done = byExercise.get(target.exerciseId) ?? [];
    const working = done.filter((set) => set.setType !== 'warmup').length;
    const allowed = target.targetSets + (extras[target.exerciseId] ?? 0);
    if (working >= allowed) return null;
    const stored = drafts[target.exerciseId];
    if (stored !== undefined) return stored;
    const best = bests.get(target.exerciseId);
    const point = best?.history[0];
    return defaultDraft(
      target,
      done[done.length - 1],
      point?.topSetWeightKg,
      point?.topSetReps,
      working,
    );
  }

  async function handleDone(target: LocalSessionTarget, draft: SetDraft): Promise<void> {
    if (session == null || !validDraft(draft)) {
      setError(
        isCardioTarget(target)
          ? 'Revisa la duración y la distancia antes de guardar la serie.'
          : 'Revisa el peso y las repeticiones antes de guardar la serie.',
      );
      return;
    }
    setError(null);
    setSaving(target.exerciseId);
    try {
      await logSet(session, {
        exerciseId: target.exerciseId,
        exerciseName: target.exerciseName,
        exerciseMedia: target.exerciseMedia,
        setNumber: (byExercise.get(target.exerciseId)?.length ?? 0) + 1,
        setType: draft.setType,
        reps: draft.reps,
        weightKg: draft.weightKg,
        rpe: draft.rpe,
        durationSeconds: draft.durationSeconds,
        distanceM: draft.distanceM,
        unilateral: target.unilateral === true,
        exerciseCategory: target.exerciseCategory ?? null,
      });
      setDrafts((current) => {
        // Con objetivo serie a serie (14-12-10-10) la siguiente no repite la
        // anterior: se suelta el borrador y `defaultDraft` propone la que toca.
        if ((target.targetSetsDetail?.length ?? 0) > 0) {
          const next = { ...current };
          delete next[target.exerciseId];
          return next;
        }
        return { ...current, [target.exerciseId]: draft };
      });
      timer.start(target.restSeconds ?? DEFAULT_REST_SECONDS);
      // Para el recorrido guiado: «Hecho» ha guardado una serie de verdad.
      emitTourSignal('set-logged');
    } catch (cause) {
      // Sin esto el fallo se perdía en un texto técnico y la serie, en silencio.
      setError(
        `No se ha podido guardar la serie en este dispositivo, así que no está registrada. Vuelve a pulsar «Hecho»; si sigue fallando, apúntala aparte. (Detalle: ${cause instanceof Error ? cause.message : String(cause)})`,
      );
    } finally {
      setSaving(null);
    }
  }

  async function handleFinish(): Promise<void> {
    if (session == null) return;
    // Terminar sin series dejaría un entreno vacío en el historial.
    if ((sets ?? []).length === 0) {
      setAskDiscard(true);
      return;
    }
    setFinishing(true);
    try {
      const previousBests = new Map<string, number | null>();
      for (const target of targets) {
        const best = bests.get(target.exerciseId);
        if (best !== undefined) previousBests.set(target.exerciseId, best.best1RmKg);
      }
      setSummary(summarizeSession(sets ?? [], previousBests));
      timer.skip();
      await finishSession(session.clientId);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setFinishing(false);
    }
  }

  return (
    <>
      <SessionHeader
        session={session}
        setsCount={sets.length}
        volumeKg={totalVolume}
        onFinish={() => void handleFinish()}
        finishing={finishing}
      />

      {/* Pantalla ancha (`xl`): el registro de series en el centro y, a la derecha, una
          columna fija con el descanso, el resumen y el índice de ejercicios. */}
      <div className="xl:grid xl:grid-cols-[minmax(0,1fr)_21rem] xl:items-start xl:gap-6">
        <div className="flex flex-col gap-3 pb-24 xl:pb-0">
          {error !== null ? <ErrorBanner message={error} /> : null}
          <SyncBadge />

          {targets.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-line bg-surface-raised px-4 py-8 text-center text-sm text-ink-muted">
              Entrenamiento libre: añade el primer ejercicio para empezar.
            </p>
          ) : null}

          {targets.map((target) => (
            <ExerciseBlock
              key={target.exerciseId}
              target={target}
              sets={byExercise.get(target.exerciseId) ?? []}
              workingCount={
                (byExercise.get(target.exerciseId) ?? []).filter((set) => set.setType !== 'warmup')
                  .length
              }
              draft={draftFor(target)}
              onDraftChange={(draft) =>
                setDrafts((current) => ({ ...current, [target.exerciseId]: draft }))
              }
              onDone={() => {
                const draft = draftFor(target);
                if (draft !== null) void handleDone(target, draft);
              }}
              onAddSet={() =>
                setExtras((current) => ({
                  ...current,
                  [target.exerciseId]: (current[target.exerciseId] ?? 0) + 1,
                }))
              }
              onRemoveSet={(set) => void removeSet(set)}
              saving={saving === target.exerciseId}
              best={bests.get(target.exerciseId)}
            />
          ))}

          {searching ? (
            <ExerciseQuickSearch
              excludeIds={new Set(targets.map((target) => target.exerciseId))}
              onClose={() => setSearching(false)}
              onPick={(exercise) => {
                void addSessionTarget(clientId, exercise);
                setSearching(false);
              }}
            />
          ) : (
            <button
              type="button"
              onClick={() => setSearching(true)}
              className="flex min-h-13 w-full items-center justify-center gap-2 rounded-xl border border-line bg-surface-raised text-sm font-semibold text-ink transition-colors hover:bg-surface-overlay active:bg-surface-overlay"
            >
              <Plus className="size-5" aria-hidden="true" />
              Añadir ejercicio
            </button>
          )}

          <Button
            size="lg"
            block
            variant="secondary"
            loading={finishing}
            onClick={() => void handleFinish()}
          >
            Terminar entrenamiento
          </Button>
        </div>

        {/* Hasta `xl` este contenedor no existe para la maqueta (`contents`) y el
          descanso sigue siendo la barra fija de abajo; en `xl` es la
          columna lateral y el descanso, su primera tarjeta. */}
        <div className="contents xl:sticky xl:top-28 xl:flex xl:flex-col xl:gap-3">
          <RestTimerBar timer={timer} />
          <SessionSidePanel
            className="hidden xl:flex"
            session={session}
            targets={targets}
            workingCounts={workingCounts}
            setsCount={sets.length}
            volumeKg={totalVolume}
            resting={timer.running}
          />
        </div>
      </div>

      {askDiscard || blocked ? (
        <DiscardSessionDialog
          onDiscard={() => void handleDiscard()}
          onKeep={keepSession}
          discarding={discarding}
          error={discardError}
        />
      ) : null}
    </>
  );
}
